import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScienceIcon from "@mui/icons-material/Science";
import ConfirmDialog from "../../components/ConfirmDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const CSV_COLUMNS = [
  { field: "preFinanceId", headerName: "PreFinanceId" },
  { field: "entityNumber", headerName: "EntityNumber" },
  { field: "preFinanceType", headerName: "PreFinanceType" },
  { field: "action", headerName: "Action" },
  { field: "migrationPath", headerName: "MigrationPath" },
  { field: "mismatchReason", headerName: "MismatchReason" },
  { field: "targetPreFinanceCode", headerName: "TargetPreFinanceCode" },
  { field: "currentPreFinanceCode", headerName: "CurrentPreFinanceCode" },
  { field: "derivedClaimId", headerName: "DerivedClaimId" },
  { field: "derivedClaimCode", headerName: "DerivedClaimCode" },
  { field: "sharedClaimPreFinanceIds", headerName: "SharedClaimPreFinanceIds" },
  { field: "linkedInvoiceCount", headerName: "LinkedInvoiceCount" },
  { field: "linkedExciseClaimCount", headerName: "LinkedExciseClaimCount" },
  { field: "derivedCountryId", headerName: "DerivedCountryId" },
  { field: "derivedYear", headerName: "DerivedYear" },
  { field: "derivedStartMonth", headerName: "DerivedStartMonth" },
  { field: "derivedEndMonth", headerName: "DerivedEndMonth" },
  { field: "derivedVersion", headerName: "DerivedVersion" },
  { field: "derivedPeriodType", headerName: "DerivedPeriodType" },
];

const escapeCsvField = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  if (text.includes(";") || text.includes("\"") || text.includes("\n")) {
    return `"${text.replace(/"/g, "\"\"")}"`;
  }
  return text;
};

const formatSharedClaimIds = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value || "";
};

const downloadReportCsv = (report) => {
  const rows = [
    CSV_COLUMNS.map((column) => column.headerName).join(";"),
    ...(report.items || []).map((item) =>
      CSV_COLUMNS.map((column) => {
        const value =
          column.field === "sharedClaimPreFinanceIds"
            ? formatSharedClaimIds(item.sharedClaimPreFinanceIds)
            : item[column.field];
        return escapeCsvField(value);
      }).join(";")
    ),
  ];
  const csv = `\uFEFF${rows.join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const fileName = report.dryRun ? "prefinance-migration-dryrun.csv" : "prefinance-migration-report.csv";
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const SummaryCard = ({ label, value, color }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" color={color || "text.primary"}>
        {value ?? 0}
      </Typography>
    </CardContent>
  </Card>
);

const PrefinanceLegacyMigration = () => {
  const { hasPermission } = usePermissions();
  const canMigrateLegacy = hasPermission(CAPABILITIES.adminMigrateLegacy, "X");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const columns = useMemo(
    () => [
      { field: "preFinanceId", headerName: "Id", width: 90 },
      { field: "entityNumber", headerName: "Customer", width: 120 },
      { field: "preFinanceType", headerName: "Product", width: 100 },
      { field: "action", headerName: "Action", width: 140 },
      { field: "migrationPath", headerName: "Path", width: 160 },
      { field: "mismatchReason", headerName: "Mismatch reason", flex: 1, minWidth: 180 },
      { field: "currentPreFinanceCode", headerName: "Current code", width: 180 },
      { field: "targetPreFinanceCode", headerName: "Target code", width: 190 },
      { field: "derivedClaimCode", headerName: "Claim code", width: 180 },
      { field: "linkedInvoiceCount", headerName: "Invoices", width: 100 },
      { field: "linkedExciseClaimCount", headerName: "Excise", width: 100 },
      { field: "derivedCountryId", headerName: "Country", width: 90 },
      { field: "derivedYear", headerName: "Year", width: 80 },
      { field: "derivedStartMonth", headerName: "From", width: 80 },
      { field: "derivedEndMonth", headerName: "To", width: 80 },
      { field: "derivedVersion", headerName: "Version", width: 90 },
    ],
    []
  );

  const handleRunMigration = async (dryRun) => {
    if (!canMigrateLegacy) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post("/api/v1/preFinance/migrateLegacy", null, {
        params: { dryRun },
        timeout: 0,
      });
      setReport(response.data);
      setSuccess(
        dryRun
          ? "Dry-run completed. No records were changed."
          : "Migration completed. Review the counts and leftover mismatches below."
      );
    } catch (requestError) {
      console.error("Prefinance legacy migration failed:", requestError);
      setError(
        requestError.response?.data?.message ||
          requestError.response?.data ||
          "The prefinance migration could not be completed."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = () => {
    handleRunMigration(true);
  };

  const handleDownloadCsv = () => {
    if (!report) {
      return;
    }

    downloadReportCsv(report);
  };

  const handleExecute = () => {
    setConfirmDialog({
      isOpen: true,
      title: "Run prefinance migration",
      subTitle:
        "This writes codes and legacy flags to the database. Dry-run first if you have not reviewed the results. Continue?",
      onConfirm: () => {
        setConfirmDialog((currentDialog) => ({ ...currentDialog, isOpen: false }));
        handleRunMigration(false);
      },
    });
  };

  if (!canMigrateLegacy) {
    return (
      <Alert severity="warning">The admin.migrateLegacy permission is required to run this migration.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Prefinance migration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Start with a dry-run to inspect historical dossiers. Download the CSV, then execute when the counts look
        correct. The job is idempotent: already migrated and already marked legacy records are skipped.
      </Typography>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}
          onClick={handleDryRun}
          disabled={loading}
          aria-label="Run prefinance migration dry-run"
        >
          Run dry-run
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloudDownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={!report || loading}
          aria-label="Download prefinance migration CSV"
        >
          Download CSV
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<PlayArrowIcon />}
          onClick={handleExecute}
          disabled={loading}
          aria-label="Execute prefinance migration"
        >
          Execute migration
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {report && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Chip
              label={report.dryRun ? "Dry-run" : "Executed"}
              color={report.dryRun ? "info" : "success"}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {report.items?.length || 0} rows in this report
            </Typography>
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Total" value={report.totalPreFinanceCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Already migrated" value={report.alreadyMigratedCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="To migrate" value={report.toMigrateCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Full migrate" value={report.fullMigrateCount} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Legacy mismatch" value={report.legacyMismatchCount} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                label="Orphan links"
                value={(report.orphanedInvoiceLinkCount || 0) + (report.orphanedExciseClaimLinkCount || 0)}
                color={
                  (report.orphanedInvoiceLinkCount || 0) + (report.orphanedExciseClaimLinkCount || 0) > 0
                    ? "error.main"
                    : "text.primary"
                }
              />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <DataGrid
                autoHeight
                getRowId={(row) => row.preFinanceId}
                rows={report.items || []}
                columns={columns}
                density="compact"
                disableSelectionOnClick
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                  },
                }}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                }}
              />
            </CardContent>
          </Card>
        </>
      )}

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </Box>
  );
};

export default PrefinanceLegacyMigration;
